import { useMemo, useState } from "react";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconChartBar,
  IconClipboardText,
  IconGift,
  IconMail,
  IconRefresh,
} from "@tabler/icons-react";
import {
  HOME_SCREEN_SECTIONS_METADATA,
  HOME_SCREEN_SECTION_KEYS,
} from "@congrs/schemas";

import { usePatchEvent } from "../../../../api/event";
import { useGetAllFeatureFlags } from "../../../../api/featureFlags";
import MobilePreview from "../MobilePreview";
import agendaSectionSettings from "./HomeScreenAgendaSettings";
import HomeScreenSectionCard from "./HomeScreenSectionCard";
import speakerSectionSettings from "./HomeScreenSpeakerSettings";
import sponsorSectionSettings from "./HomeScreenSponsorSettings";

// The sections that hold a place in the list. The rest are action chips drawn
// inside a parent section, and are switched on and off from its card.
const MOVABLE = HOME_SCREEN_SECTION_KEYS.filter(
  (key) => !HOME_SCREEN_SECTIONS_METADATA[key].chipOf
);
const CHIP_KEYS = HOME_SCREEN_SECTION_KEYS.filter(
  (key) => HOME_SCREEN_SECTIONS_METADATA[key].chipOf
);
const chipsOf = (parent) =>
  CHIP_KEYS.filter(
    (key) => HOME_SCREEN_SECTIONS_METADATA[key].chipOf === parent
  );
const QUICK_ACTION_KEYS = chipsOf("quickActions");

/**
 * The sections that have settings of their own, keyed by section. A section
 * that isn't listed here is a plain row — visibility and position only.
 *
 * An entry carries `defaults` — what the app falls back to with nothing saved,
 * so it has to match the section's own fallback there — plus a `Settings` and
 * a `Summary` component. Both get `{ eventId, options }` and fetch whatever
 * they need themselves; nothing below knows what a section's options mean.
 * Adding a section means writing those two and listing it here.
 *
 * A section whose settings hold a list of their own can't open a drag context
 * inside this one, so it renders a `Droppable` keyed by its section key and
 * exports `reorder(options, from, to)`; `onDragEnd` routes the drop back to it.
 */
const SECTION_SETTINGS = {
  agenda: agendaSectionSettings,
  speakers: speakerSectionSettings,
  sponsors: sponsorSectionSettings,
};

/**
 * The saved arrangement: the sections named in `order` in that sequence, then
 * whatever `order` left out. Mirrors `arrangeHomeSections` in mobile-next, so
 * the list reads in the order the app will render.
 */
const arrange = (homeScreen) => {
  const order = (homeScreen?.order ?? []).filter((key) =>
    MOVABLE.includes(key)
  );
  const rest = MOVABLE.filter((key) => !order.includes(key));

  return [...order, ...rest];
};

const buildRows = (homeScreen) => {
  const hidden = homeScreen?.hidden ?? [];

  return arrange(homeScreen).map((key) => ({
    key,
    visible: !hidden.includes(key),
  }));
};

/** The chips a parent section carries, by key — visibility only. */
const buildChips = (homeScreen) => {
  const hidden = homeScreen?.hidden ?? [];

  return Object.fromEntries(
    CHIP_KEYS.map((key) => [key, !hidden.includes(key)])
  );
};

const buildOptions = (homeScreen) =>
  Object.fromEntries(
    Object.entries(SECTION_SETTINGS).map(([key, { defaults }]) => [
      key,
      { ...defaults, ...homeScreen?.options?.[key] },
    ])
  );

/** Every section visible and unconfigured — what the app falls back to. */
const DEFAULT_STATE = {
  rows: buildRows(null),
  chips: buildChips(null),
  options: buildOptions(null),
};

/**
 * The draft as the app reads it. Used for saving and for the preview alike, so
 * what the preview shows is exactly what saving would produce. Chips have no
 * place in `order` — the app draws them inside their parent section.
 */
const toHomeScreenConfig = (draft) => ({
  order: draft.rows.map((row) => row.key),
  hidden: [
    ...draft.rows.filter((row) => !row.visible).map((row) => row.key),
    ...Object.keys(draft.chips).filter((key) => !draft.chips[key]),
  ],
  options: draft.options,
});

const CHIP_ICONS = {
  giveaway: { Icon: IconGift, color: "pink" },
  polls: { Icon: IconChartBar, color: "blue" },
  surveys: { Icon: IconClipboardText, color: "gray" },
  specialInvitations: { Icon: IconMail, color: "orange" },
};

/**
 * The chips a parent section carries. They have no order of their own — the
 * app shows whichever ones have something waiting — so this is visibility
 * alone, and a chip whose feature is off for the event says so instead.
 */
const SectionChips = ({ keys, chips, isDisabled, onToggle }) => (
  <Group gap="sm" mt="md" pl={34}>
    {keys.map((key) => {
      const { Icon, color } = CHIP_ICONS[key];
      const disabled = isDisabled(key);

      return (
        <Card key={key} withBorder px="md" py={8} radius="xl">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              variant="light"
              color={disabled ? "gray" : color}
              radius="xl"
              size="md"
            >
              <Icon size={16} />
            </ThemeIcon>
            <Text fw={500} size="sm" c={disabled ? "dimmed" : undefined}>
              {HOME_SCREEN_SECTIONS_METADATA[key].label}
            </Text>
            {disabled ? (
              <Text size="xs" c="dimmed">
                Off in Features
              </Text>
            ) : (
              <Switch
                size="sm"
                checked={chips[key]}
                onChange={() => onToggle(key)}
              />
            )}
          </Group>
        </Card>
      );
    })}
  </Group>
);

/** What the quick actions row says about itself while collapsed. */
const summarizeChips = (chips, isDisabled) => {
  const on = QUICK_ACTION_KEYS.filter(
    (key) => chips[key] && !isDisabled(key)
  ).map((key) => HOME_SCREEN_SECTIONS_METADATA[key].label);

  return on.length > 0
    ? `${on.join(" · ")} — each shown only while there's something to do.`
    : "No chips switched on.";
};

const HomeScreenTab = ({ data }) => {
  const { data: flags } = useGetAllFeatureFlags({ eventId: data.id });
  const { mutate, isPending: isSaving } = usePatchEvent(data.id);

  const homeScreen = data?.config?.homeScreen;
  const saved = useMemo(
    () => ({
      rows: buildRows(homeScreen),
      chips: buildChips(homeScreen),
      options: buildOptions(homeScreen),
    }),
    [homeScreen]
  );
  const [draft, setDraft] = useState(saved);

  const isDisabled = (key) => {
    if (key === "quickActions") return QUICK_ACTION_KEYS.every(isDisabled);

    const feature = HOME_SCREEN_SECTIONS_METADATA[key].feature;
    // Nothing is off until the flags actually arrive — a premature "turned
    // off" badge claims something about the event that may not be true.
    if (!feature || !flags) return false;

    return !flags.find((flag) => flag.featureName === feature)?.enabled;
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const isAtDefault = JSON.stringify(draft) === JSON.stringify(DEFAULT_STATE);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    // A drag inside a section's own settings list — the droppable is named
    // after the section, and the section knows what the move means.
    const section = SECTION_SETTINGS[result.source.droppableId];
    if (section) {
      setOptions(
        result.source.droppableId,
        section.reorder(
          draft.options[result.source.droppableId],
          result.source.index,
          result.destination.index
        )
      );

      return;
    }

    const to = result.destination.index;
    if (to === result.source.index) return;

    setDraft((current) => {
      const rows = Array.from(current.rows);
      const [moved] = rows.splice(result.source.index, 1);
      rows.splice(to, 0, moved);

      return { ...current, rows };
    });
  };

  const toggle = (key) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.key === key ? { ...row, visible: !row.visible } : row
      ),
    }));

  const toggleChip = (key) =>
    setDraft((current) => ({
      ...current,
      chips: { ...current.chips, [key]: !current.chips[key] },
    }));

  const setOptions = (key, options) =>
    setDraft((current) => ({
      ...current,
      options: { ...current.options, [key]: options },
    }));

  /**
   * A row's summary line and its expandable settings, or nothing for a row
   * that is visibility and position only. A parent of chips is its own case:
   * what it holds is chip visibility, not the options other sections keep.
   */
  const sectionExtras = (key) => {
    const chipKeys = chipsOf(key);
    if (chipKeys.length > 0) {
      return {
        description:
          key === "quickActions"
            ? summarizeChips(draft.chips, isDisabled)
            : undefined,
        settings: (
          <SectionChips
            keys={chipKeys}
            chips={draft.chips}
            isDisabled={isDisabled}
            onToggle={toggleChip}
          />
        ),
      };
    }

    const section = SECTION_SETTINGS[key];
    if (!section) return {};

    const options = draft.options[key];

    return {
      description: <section.Summary eventId={data.id} options={options} />,
      settings: (
        <section.Settings
          eventId={data.id}
          options={options}
          onChange={(next) => setOptions(key, next)}
        />
      ),
    };
  };

  const previewConfig = useMemo(() => toHomeScreenConfig(draft), [draft]);

  const onSave = () => mutate({ config: { homeScreen: previewConfig } });

  return (
    <Container py="md" size="xl">
      <Flex
        gap="lg"
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "flex-start" }}
      >
        <Box flex={1} miw={0}>
          <Paper withBorder shadow="md" p="md" px="lg" radius="md">
            <Stack gap="xs">
              <Title order={3}>Home screen sections</Title>
              <Text c="dimmed" size="sm" mb="md">
                Choose which sections attendees see on the app home screen, and
                in what order. Drag to reorder. A section with nothing to show
                is skipped in the app, and drawn greyed out in the preview so
                you can see where it would sit.
              </Text>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="home-screen-sections">
                  {(provided) => (
                    <Stack
                      gap="sm"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {draft.rows.map((row, index) => {
                        const { description, settings } = sectionExtras(
                          row.key
                        );

                        return (
                          <HomeScreenSectionCard
                            key={row.key}
                            sectionKey={row.key}
                            index={index}
                            visible={row.visible}
                            disabled={isDisabled(row.key)}
                            onToggle={() => toggle(row.key)}
                            description={description}
                            settings={settings}
                          />
                        );
                      })}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>

              <Divider mt="xs" />

              {/* Reset sits apart from the other two: it discards more than
                  this session's edits, so it shouldn't be next to "Discard".
                  The preview panel carries the unsaved badge. */}
              <Group justify="space-between" align="center">
                <Button
                  variant="subtle"
                  color="gray"
                  leftSection={<IconRefresh size={16} />}
                  disabled={isAtDefault || isSaving}
                  onClick={() => setDraft(DEFAULT_STATE)}
                >
                  Reset to default
                </Button>

                <Group gap="xs">
                  <Button
                    variant="default"
                    disabled={!isDirty || isSaving}
                    onClick={() => setDraft(saved)}
                  >
                    Discard
                  </Button>
                  <Button
                    disabled={!isDirty}
                    loading={isSaving}
                    onClick={onSave}
                  >
                    Save changes
                  </Button>
                </Group>
              </Group>
            </Stack>
          </Paper>
        </Box>

        {/* Below `lg` there is no room for the frame beside the settings it
            explains, so it moves under them rather than disappearing. */}
        <MobilePreview
          eventId={data.id}
          config={{ homeScreen: previewConfig }}
          isDirty={isDirty}
        />
      </Flex>
    </Container>
  );
};

export default HomeScreenTab;
